import React from 'react';
import { CardSvgIcon } from '../../InformationCard.types';
import Avatar, { AvatarProps } from '@mui/material/Avatar';

/**
 * `ImageOrIcon` Component
 *
 * A versatile component that renders either an `Avatar` with an image source or an `SvgIcon`
 * depending on the type of `src` prop provided. This component determines whether to render an image
 * or an icon based on the value of the `src` prop.
 *
 * @remarks
 * - If the `src` prop is a string, it renders an `Avatar` component with the image URL.
 * - If the `src` prop is a React component (specifically a `CardSvgIcon`), it renders the icon.
 * - The component can be used to display either an image or an SVG icon in the same interface.
 * - This component passes down all the `AvatarProps` to the `Avatar` component if an image is rendered.
 *
 * @param {AvatarProps & { key?: string | number; color?: string }} props - The props to configure the component.
 * Includes the `src` prop, which determines whether an image or icon is rendered.
 *
 * @returns {React.ReactElement} Either an `Avatar` component or an `SvgIcon` component based on the `src` prop type.
 *
 * @example
 * ```tsx
 * import ImageOrIcon from './ImageOrIcon';
 * import { ReactComponent as SampleIcon } from './sample-icon.svg';
 *
 * // Example usage with an image
 * <ImageOrIcon src="https://example.com/image.jpg" color="blue" />
 *
 * // Example usage with an icon
 * <ImageOrIcon src={SampleIcon} color="red" />
 * ```
 *
 * @category InformationCard
 * @document ImageOrIconComponentUsage.md
 */
export const ImageOrIcon = (
  props: AvatarProps & { key?: string | number; color?: string },
): React.ReactElement => {
  if (typeof props.src === 'string') {
    // Render Avatar component if src is an image URL
    return <Avatar {...props} key={`${props.src}`} />;
  } else {
    // Render SvgIcon if src is a React component (CardSvgIcon)
    const Svg = props.src as unknown as CardSvgIcon;
    return <Svg key={`${props.src}`} className={props.className} />;
  }
};
